import type { OrbColor } from "./VariableState";

/**
 * 比較演算子
 */
export type ComparisonOperator = "==" | "!=" | ">" | "<";

/**
 * 条件の型（Discriminated Union）
 *
 * Phase 6では壁判定とオーブ比較をサポート。
 * 将来の拡張時は新しい type を追加するだけでよい。
 */
export type Condition =
  | { type: "wallAhead" }
  | { type: "noWallAhead" }
  | {
      type: "orbComparison";
      orbColor: OrbColor;
      operator: ComparisonOperator;
      value: number;
    };

/**
 * 条件評価に必要なコンテキスト
 *
 * Familiar, TileMap, VariableState の具体的なクラスに依存せず、
 * 必要な情報だけを受け取る。テスト時にモックしやすい。
 */
export interface ConditionContext {
  /** 使い魔の前方1マスの座標 */
  forwardPosition: { x: number; y: number };
  /** 指定座標が歩行可能か判定 */
  isWalkable: (x: number, y: number) => boolean;
  /** 指定色のオーブの現在値を取得 */
  getOrbValue: (color: OrbColor) => number;
}

/**
 * 条件を評価する純粋関数
 */
export function evaluateCondition(
  condition: Condition,
  ctx: ConditionContext
): boolean {
  switch (condition.type) {
    case "wallAhead":
      return !ctx.isWalkable(ctx.forwardPosition.x, ctx.forwardPosition.y);

    case "noWallAhead":
      return ctx.isWalkable(ctx.forwardPosition.x, ctx.forwardPosition.y);

    case "orbComparison":
      return compareValues(
        ctx.getOrbValue(condition.orbColor),
        condition.operator,
        condition.value
      );
  }
}

/**
 * 比較演算を実行
 */
function compareValues(
  left: number,
  operator: ComparisonOperator,
  right: number
): boolean {
  switch (operator) {
    case "==":
      return left === right;
    case "!=":
      return left !== right;
    case ">":
      return left > right;
    case "<":
      return left < right;
  }
}

/** オーブ色の日本語ラベル */
const ORB_LABELS: Record<OrbColor, string> = {
  green: "緑",
  cyan: "水色",
  orange: "オレンジ",
};

/** 比較演算子の日本語ラベル */
export const OPERATOR_LABELS: Record<ComparisonOperator, string> = {
  "==": "＝",
  "!=": "≠",
  ">": "＞",
  "<": "＜",
};

/**
 * 条件の日本語ラベルを取得（UI表示用）
 */
export function getConditionLabel(condition: Condition): string {
  switch (condition.type) {
    case "wallAhead":
      return "前に壁がある";
    case "noWallAhead":
      return "前に壁がない";
    case "orbComparison":
      return `${ORB_LABELS[condition.orbColor]}${OPERATOR_LABELS[condition.operator]}${condition.value}`;
  }
}
